import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  LuSearch, LuTrash2, LuDownload, LuTarget, LuCalendar, LuMail, LuPhone, 
  LuFilter, LuCircleCheck, LuUser, LuClock, LuHistory
} from 'react-icons/lu';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const LeadManagement = ({ onCountChange }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  
  // States for selection and filtering
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(leads.length);
    }
  }, [leads.length, onCountChange]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await axios.get(`${apiUrl}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setLeads(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      await axios.delete(`${apiUrl}/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.filter(lead => lead._id !== id));
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } catch (err) {
      console.error('Error deleting lead:', err);
      alert('Failed to delete lead');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} leads?`)) return;

    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      for (const id of selectedIds) {
        await axios.delete(`${apiUrl}/api/leads/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setLeads(leads.filter(lead => !selectedIds.includes(lead._id)));
      setSelectedIds([]);
      setSelectionMode(false);
    } catch (err) {
      console.error('Error in batch delete:', err);
      alert('Failed to complete batch delete');
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.whatsapp && lead.whatsapp.includes(searchTerm))
    );
  }, [leads, searchTerm]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredLeads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLeads.map(l => l._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const exportToCSV = () => {
    const dataToExport = selectedIds.length > 0 
      ? filteredLeads.filter(l => selectedIds.includes(l._id))
      : filteredLeads;

    const headers = ['Name', 'Email', 'WhatsApp', 'Source', 'Date', 'Time'];
    const rows = dataToExport.map(lead => [
      lead.name,
      lead.email,
      lead.whatsapp || 'N/A',
      lead.source,
      moment(lead.createdAt).format('YYYY-MM-DD'),
      moment(lead.createdAt).format('HH:mm:ss')
    ]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `landing_leads_${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={6} />;
  }

  return (
    <div className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Leads', value: leads.length, icon: LuTarget, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
            { label: 'Captured Today', value: leads.filter(r => moment(r.createdAt).isSame(moment(), 'day')).length, icon: LuClock, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
            { label: 'With WhatsApp', value: leads.filter(r => r.whatsapp).length, icon: LuPhone, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
            { label: 'Filtered', value: filteredLeads.length, icon: LuHistory, iconBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-600 dark:text-purple-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or whatsapp..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${
                  selectionMode 
                  ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" 
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                <LuCircleCheck className={selectionMode ? "animate-pulse" : ""} />
                {selectionMode ? "Selection Active" : "Selection Mode"}
              </button>
              
              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <LuDownload size={18} />
                Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
              </button>

              {selectionMode && selectedIds.length > 0 && (
                <button
                  onClick={handleBatchDelete}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30 whitespace-nowrap"
                >
                  Delete ({selectedIds.length})
                </button>
              )}
            </div>
          </div>
        </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                {selectionMode && (
                  <th className="px-4 py-4 text-center w-12">
                    <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === filteredLeads.length && filteredLeads.length > 0} className="rounded" />
                  </th>
                )}
                <th className="px-4 py-4 text-left text-sm font-semibold">Lead Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">WhatsApp</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Source</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 7 : 6} className="px-6 py-16 text-center text-gray-500 font-bold">
                    No leads found matching your search.
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr 
                    key={lead._id} 
                    className={`group transition-all duration-200 hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer ${selectedIds.includes(lead._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(lead._id) : null}
                  >
                    {selectionMode && (
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleSelectOne(lead._id)}
                          className={`p-1.5 rounded-lg transition-colors ${selectedIds.includes(lead._id) ? 'text-primary-600 bg-primary-100' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          <LuCircleCheck className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/30 dark:to-primary-900/10 flex items-center justify-center flex-shrink-0 border border-primary-200/50">
                          <LuUser className="text-primary-600 dark:text-primary-400 w-3.5 h-3.5" />
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">{lead.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                        <LuMail className="text-gray-400 w-3 h-3" />
                        {lead.email}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {lead.whatsapp ? (
                        <div className="flex items-center gap-2">
                          <LuPhone className="text-emerald-500 w-3 h-3" />
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{lead.whatsapp}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not provided</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                        {lead.source}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-400">
                        <LuCalendar className="w-3 h-3 text-gray-400" />
                        {moment(lead.createdAt).format('MMM DD, YYYY')}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 group-hover:bg-primary-50/20 dark:group-hover:bg-primary-900/10 transition-colors shadow-left">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(lead._id); }}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <LuTrash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadManagement;
